// Ported from packages/desk/src/mobile/SearchSheet.tsx (docs/PLAN-2026-09-21-mobile-os-shell.md, step 4). Same file name and props
// as the desk copy, the dk- prefix renamed rdr-, so a fix in one is a copy in the other. Radar never imports @gamo/desk.
/**
 * Search: one field over the apps first, then whatever the host can search (classes and students for a school). Every
 * searcher reads data the host already has; typing never makes a request.
 */
import { useId, useState } from "react";
import type { RadarAppDefinition } from "../../apps/registry";
import { ChevronGlyph } from "./glyphs";
import { searchApps } from "./layout";
import { Sheet } from "./Sheet";
import type { MobileSearchResult, MobileSearcher } from "./types";

/** At most this many rows per group, so the first screen shows every group. */
export const RESULTS_PER_GROUP = 6;

export function searchAll(apps: RadarAppDefinition[], searchers: MobileSearcher[], q: string, onOpenApp: (a: RadarAppDefinition) => void): Array<{ id: string; label: string; results: MobileSearchResult[] }> {
  if (!q.trim()) return [];
  const groups: Array<{ id: string; label: string; results: MobileSearchResult[] }> = [{ id: "apps", label: "Apps", results: searchApps(apps, q).map((a) => ({ id: a.id, title: a.name, icon: a.icon, run: () => onOpenApp(a) })) }];
  for (const s of searchers) groups.push({ id: s.id, label: s.label, results: s.search(q) });
  return groups.filter((g) => g.results.length > 0).map((g) => ({ ...g, results: g.results.slice(0, RESULTS_PER_GROUP) }));
}

export function SearchSheet({
  open,
  apps,
  searchers,
  onClose,
  onOpenApp,
}: {
  open: boolean;
  apps: RadarAppDefinition[];
  searchers: MobileSearcher[];
  onClose: () => void;
  onOpenApp: (a: RadarAppDefinition) => void;
}) {
  const titleId = useId();
  const [q, setQ] = useState("");
  const close = () => {
    setQ("");
    onClose();
  };
  const groups = searchAll(apps, searchers, q, onOpenApp);
  const what = ["apps", ...searchers.map((s) => s.label.toLowerCase())];
  const hint = what.length > 1 ? `${what.slice(0, -1).join(", ")} and ${what.at(-1)}` : what[0];
  return (
    <Sheet
      open={open}
      onClose={close}
      title="Search"
      titleId={titleId}
      data="search"
      className="rdr-msearch"
      header={
        <input
          className="rdr-msearch-field"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${hint}`}
          aria-label="Search"
          data-autofocus=""
          enterKeyHint="search"
        />
      }
    >
      {!q.trim() ? (
        <p className="rdr-mempty">Search {hint}.</p>
      ) : groups.length === 0 ? (
        <p className="rdr-mempty" role="status">
          Nothing found for “{q.trim()}”.
        </p>
      ) : (
        groups.map((g) => (
          <section key={g.id} className="rdr-mresults" aria-label={g.label}>
            <h3 className="rdr-mlib-label">{g.label}</h3>
            <ul>
              {g.results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="rdr-mrow"
                    onClick={() => {
                      close();
                      r.run();
                    }}
                  >
                    {r.icon && (
                      <span className="rdr-mrow-ic" aria-hidden="true">
                        {r.icon}
                      </span>
                    )}
                    <span className="rdr-mrow-text">
                      <span className="rdr-mrow-title">{r.title}</span>
                      {r.subtitle && <span className="rdr-mrow-sub">{r.subtitle}</span>}
                    </span>
                    <span className="rdr-mrow-go" aria-hidden="true">
                      <ChevronGlyph />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </Sheet>
  );
}
